import React, { useState, useEffect, useRef } from "react";
import { toast } from "react-toastify";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import FormControl from "@material-ui/core/FormControl";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import api from "../../services/api";
import {
  ModalTitle,
  FormField,
  Callout,
  usePanelStyles
} from "../QueueIntegrationModal/panelKit";

const ACOES = [
  { value: "SB", label: "2ª via de fatura (todas em aberto)" },
  { value: "VE", label: "Faturas vencidas" },
  { value: "AV", label: "Faturas a vencer" },
  { value: "LC", label: "Desbloqueio de confiança" },
  { value: "ST", label: "Status da conexão" },
  { value: "", label: "Usar a ação configurada na integração" }
];

const IA_TYPES = ["ISP_IA", "HOSPEDAGEM_IA"];
const isIa = integration => IA_TYPES.includes(String(integration?.type || "").toUpperCase());
const isHospedagemIa = integration =>
  String(integration?.type || "").toUpperCase() === "HOSPEDAGEM_IA";

/**
 * Modal do nó "Integração ERP (SGP/IXC)": escolhe a integração cadastrada em
 * Filas & Chatbot → Integrações e a ação (2ª via, desbloqueio, status...).
 * Ao chegar neste nó o fluxo pede o CPF e o chatbot do ERP assume o atendimento.
 */
const FlowBuilderErpModal = ({ open, onSave, data, onUpdate, close }) => {
  const panel = usePanelStyles();
  const isMounted = useRef(true);

  const [activeModal, setActiveModal] = useState(false);
  const [integrations, setIntegrations] = useState([]);
  const [selectedIntegration, setSelectedIntegration] = useState("");
  const [acao, setAcao] = useState("SB");
  const [message, setMessage] = useState("");
  const [contexto, setContexto] = useState("");

  useEffect(() => {
    if (open === "edit" || open === "create") {
      (async () => {
        try {
          // SGP/IXC via endpoint dedicado + agentes de IA (ISP_IA) da lista geral
          const [erpRes, allRes] = await Promise.all([
            api.get("/isp/erp-integrations").catch(() => ({ data: [] })),
            api.get("/queueIntegration").catch(() => ({ data: {} }))
          ]);
          const erpList = erpRes.data?.integrations || erpRes.data || [];
          const all = allRes.data?.queueIntegrations || allRes.data || [];
          const iaList = (Array.isArray(all) ? all : []).filter(i =>
            IA_TYPES.includes(String(i.type).toUpperCase())
          );
          const seen = new Set();
          const merged = [...(Array.isArray(erpList) ? erpList : []), ...iaList].filter(i => {
            if (seen.has(i.id)) return false;
            seen.add(i.id);
            return true;
          });
          setIntegrations(merged);
        } catch (err) {
          console.log(err);
          setIntegrations([]);
        }
        if (open === "edit" && data?.data) {
          setSelectedIntegration(data.data.erpIntegrationId || "");
          setAcao(data.data.acao !== undefined ? data.data.acao : "SB");
          setMessage(data.data.message || "");
          setContexto(data.data.contexto || "");
        } else {
          setSelectedIntegration("");
          setAcao("SB");
          setMessage("");
          setContexto("");
        }
        setActiveModal(true);
      })();
    }
    return () => {
      isMounted.current = false;
    };
  }, [open, data]);

  const handleClose = () => {
    close(null);
    setActiveModal(false);
  };

  const selectedObj = integrations.find(i => i.id === selectedIntegration);

  const handleSave = () => {
    if (!selectedIntegration) {
      return toast.error("Escolha a integração ou o agente");
    }
    const integration = selectedObj;
    const payload = {
      erpIntegrationId: selectedIntegration,
      integrationName: integration ? integration.name : `#${selectedIntegration}`,
      provider: integration ? integration.type : "",
      acao: isIa(integration) ? "" : acao,
      message: message.trim(),
      contexto: isHospedagemIa(integration) ? contexto.trim() : ""
    };
    if (open === "edit") {
      onUpdate({ ...data, data: payload });
    } else {
      onSave(payload);
    }
    handleClose();
  };

  return (
    <Dialog open={activeModal} onClose={handleClose} fullWidth maxWidth="sm" scroll="paper">
      <ModalTitle
        id="form-dialog-title"
        title={open === "create" ? "Adicionar integração / agente ao fluxo" : "Editar integração / agente"}
        subtitle="Passa a conversa para o chatbot do ERP ou para um agente de IA."
        onClose={handleClose}
      />
      <DialogContent>
        <div className={panel.stack}>
          <FormField
            label="Integração ERP ou Agente de IA"
            help={
              !integrations.length
                ? "Nenhuma integração SGP/IXC cadastrada. Crie em Filas & Chatbot → Integrações."
                : undefined
            }
          >
            <FormControl variant="outlined" size="small" fullWidth>
              <Select
                id="flow-erp-integration"
                value={selectedIntegration}
                displayEmpty
                onChange={e => setSelectedIntegration(e.target.value)}
              >
                <MenuItem value="" disabled>
                  Selecione a integração cadastrada
                </MenuItem>
                {integrations.map(i => (
                  <MenuItem key={i.id} value={i.id}>
                    {isIa(i) ? "🤖 " : ""}{i.name} ({isIa(i) ? "Agente IA" : i.type})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FormField>

          {!isIa(selectedObj) && (
            <FormField label="O que este nó faz">
              <FormControl variant="outlined" size="small" fullWidth>
                <Select
                  id="flow-erp-action"
                  value={acao}
                  displayEmpty
                  onChange={e => setAcao(e.target.value)}
                >
                  {ACOES.map(a => (
                    <MenuItem key={a.value} value={a.value}>
                      {a.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </FormField>
          )}

          <FormField
            label={isIa(selectedObj) ? "Mensagem de abertura do agente" : "Mensagem pedindo o CPF"}
            optional="opcional"
            htmlFor="flow-erp-message"
          >
            <TextField
              id="flow-erp-message"
              variant="outlined"
              size="small"
              fullWidth
              multiline
              minRows={2}
              placeholder={
                isIa(selectedObj)
                  ? "Você está com o nosso *assistente virtual*. Me conta o que está acontecendo 🙂"
                  : "Para continuar, me informe seu *CPF ou CNPJ* (somente números):"
              }
              value={message}
              onChange={e => setMessage(e.target.value)}
            />
          </FormField>

          {isHospedagemIa(selectedObj) && (
            <FormField
              label="Contexto para o agente"
              optional="opcional"
              htmlFor="flow-erp-context"
              help="Enviado ao agente como contexto silencioso desta conversa (não aparece para o cliente)."
            >
              <TextField
                id="flow-erp-context"
                variant="outlined"
                size="small"
                fullWidth
                multiline
                minRows={2}
                placeholder="ex.: Cliente escolheu no menu: Casa Recanto das Ondas"
                value={contexto}
                onChange={e => setContexto(e.target.value)}
              />
            </FormField>
          )}

          <Callout icon={<InfoOutlinedIcon />}>
            {isHospedagemIa(selectedObj)
              ? "Ao chegar neste nó, o Agente Hospedagem assume a conversa: busca imóveis, verifica disponibilidade, cota e pode criar uma pré-reserva; se não conseguir resolver, transfere para a fila configurada na integração. O 'Contexto para o agente' acima é enviado como informação silenciosa (não aparece para o cliente)."
              : isIa(selectedObj)
              ? "Ao chegar neste nó, o agente de IA do setor assume a conversa: identifica o cliente, consulta o ERP (faturas, status, sinal da fibra), tenta resolver e — se não conseguir — transfere para a fila de atendentes configurada na integração. A persona/escopo do agente é o campo 'promptExtra' da integração ISP_IA."
              : "Ao chegar neste nó, o fluxo pede o CPF e o chatbot do ERP assume: identifica o cliente e executa a ação escolhida (boleto em PDF, código de barras, PIX, desbloqueio, status da conexão). Este é um nó final do fluxo."}
          </Callout>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        <Button onClick={handleSave} color="primary" variant="contained">
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FlowBuilderErpModal;
